import { useMemo } from 'react';
import { components } from '@api/club-streams/generated';
import { components as components2 } from '@api/club/generated';

import { FUNC_NOOP } from '@libs/constants';
import { useImmediateEffect } from '@libs/hooks/useImmediateEffect';
import { usePrev } from '@libs/hooks/usePrev';

import {
  TOURNAMENT_STREAM_SOCKET_URL,
  useRoundShortGamesQuery,
  useRoundShortGamesSocket,
} from '@features/shared/api/streams';

import { TournamentGamesGame, TournamentGamesProps } from '../ui/components/TournamentGames';
import { mapGameToGameCard } from '../utils/gameCardMappers';

// FIXME: перевести всё на простые RoundSocket + Mapping (discard и enabled не нужен)

/*
    ____,-------------------------------,____
    \   |             Hook              |   /
    /___|-------------------------------|___\
*/

type Value = {
  loading: boolean;
  error?: Error | null;
  games?: TournamentGamesProps['games'];
  prevGames?: TournamentGamesProps['games'];
};

export function useTournamentGames(
  tournamentId: string,
  // HACK: тут должен был быть roundNumber, но передаётся весь раунд, чтобы
  // по статусу раунда определить, когда сокет можно отключить сокет (по статусу игр
  // этого теперь сделать невозможно, т.к. сейчас доп. партии создаются в
  // тот же раунд, а не в новый, как в приличном обществе)
  round?: components2['schemas']['RoundDto'],
  { enabled } = { enabled: true },
): Value {
  const roundNumber = round?.roundNumber || NaN;
  const roundId = round?.roundId;

  const roundGamesQuery = useRoundShortGamesQuery(tournamentId, roundNumber, {
    enabled: Boolean(roundId),
    refetchOnWindowFocus: false,
  });

  const cachingStore = useMemo(
    () =>
      roundGamesQuery.data
        ? new CachingMapper(tournamentId, roundNumber, roundGamesQuery.data)
        : undefined,
    [roundGamesQuery.data, tournamentId, roundNumber],
  );

  useImmediateEffect(() => {
    const data = roundGamesQuery.data;
    if (data && cachingStore) {
      cachingStore.updateMany(data);
    }
  }, [roundGamesQuery.data]);

  useRoundShortGamesSocket({
    customSocketUrl: TOURNAMENT_STREAM_SOCKET_URL,
    tournamentId,
    roundNumber,
    cb: FUNC_NOOP, // Сокет сам обновит кэш roundGamesQuery
    enabled: Boolean(
      round && enabled && !roundGamesQuery.isFetching && round.status !== 'Finished',
    ),
  });

  const prevGames = usePrev(cachingStore?.games);

  return {
    loading: roundGamesQuery.isPending,
    games: cachingStore?.games,
    prevGames,
  };
}

/*
    ____,-------------------------------,____
    \   |             Store             |   /
    /___|-------------------------------|___\
*/

class CachingMapper {
  #dtos: Record<string, components['schemas']['GameStreamStateShort']> = {};
  #games: Record<string, TournamentGamesGame> = {};

  constructor(
    public tournamentId: string,
    public roundNumber: number,
    data: components['schemas']['GameStreamStateShort'][],
  ) {
    data
      // HACK: пересортировываем партии по дате начала вместо номера
      // доски (ибо пока в раунд могут добавится дополнительные партии на тех же
      // досках)
      .map((d) => ({ ...d, toString: () => d.startTimestampMs }))
      .sort()
      .forEach((g) => {
        this.#update(g);
      });

    this.games = Object.values(this.#games);
  }

  games: TournamentGamesGame[] = [];

  updateMany(data: components['schemas']['GameStreamStateShort'][]) {
    data.forEach((g) => {
      if (this.#dtos[g.gameId] !== g) {
        this.#update(g);
      }
    });
    this.games = Object.values(this.#games);
  }

  #update(g: components['schemas']['GameStreamStateShort']) {
    this.#dtos[g.gameId] = g;
    this.#games[g.gameId] = mapGameToGameCard(g.gameId, g);
  }
}
